import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormControlLabel, Grid, Switch, TextField } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { uploadPublicObject } from "utils/S3";
import { formatApiError } from "../utils/api";

function UploadAdornment({ field, accept, uploading, onFile }) {
  return (
    <MDButton component="label" size="small" variant="text" color="info" disabled={uploading === field}>
      {uploading === field ? "Uploading…" : "Upload"}
      <input hidden type="file" accept={accept} onChange={(event) => onFile(field, event.target.files?.[0])} />
    </MDButton>
  );
}

UploadAdornment.propTypes = {
  field: PropTypes.string.isRequired,
  accept: PropTypes.string.isRequired,
  uploading: PropTypes.string.isRequired,
  onFile: PropTypes.func.isRequired,
};

// Logo, favicon, analytics and checkout switches. `values` is the settings
// object; `onChange(field, value)` updates one key. Real estate sites have
// no checkout (visitors enquire or request an inspection), so they pass
// showCheckout={false}.
function BrandAssetFields({ values, onChange, onError, disabled, showCheckout }) {
  const { axiosInstance } = useAuth();
  const [uploading, setUploading] = useState("");

  const upload = async (field, file) => {
    if (!file || !axiosInstance) return;
    setUploading(field);
    try {
      const uploaded = await uploadPublicObject({ axiosInstance, file, featurePath: "website-branding" });
      onChange(field, uploaded.url);
    } catch (uploadError) {
      onError(formatApiError(uploadError, "The brand asset could not be uploaded."));
    } finally {
      setUploading("");
    }
  };

  const text = (field) => (event) => onChange(field, event.target.value);
  const toggle = (field) => (event) => onChange(field, event.target.checked);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          disabled={disabled}
          label="Logo URL"
          placeholder="https://…/logo.svg"
          value={values.logo_url || ""}
          onChange={text("logo_url")}
          helperText="Upload a logo or paste a public URL."
          InputProps={{ endAdornment: <UploadAdornment field="logo_url" accept="image/*" uploading={uploading} onFile={upload} /> }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          disabled={disabled}
          label="Favicon URL"
          placeholder="https://…/favicon.png"
          value={values.favicon_url || ""}
          onChange={text("favicon_url")}
          helperText="A square PNG, SVG or ICO."
          InputProps={{ endAdornment: <UploadAdornment field="favicon_url" accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon" uploading={uploading} onFile={upload} /> }}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          disabled={disabled}
          label="Analytics measurement ID"
          placeholder="G-XXXXXXXXXX"
          value={values.analytics_id || ""}
          onChange={text("analytics_id")}
          helperText="Optional. Google Analytics 4 measurement ID."
        />
      </Grid>
      {showCheckout && (
        <Grid item xs={12} md={6}>
          <MDBox sx={{ border: `1px solid ${colors.border.default}`, borderRadius: 2, px: 1.5, py: 0.5 }}>
            <FormControlLabel disabled={disabled} control={<Switch checked={Boolean(values.checkout_enabled)} onChange={toggle("checkout_enabled")} />} label="Enable website checkout" />
            <FormControlLabel disabled={disabled} control={<Switch checked={Boolean(values.stripe_enabled)} onChange={toggle("stripe_enabled")} />} label="Use PearlCRM Stripe payments" />
            <MDTypography variant="caption" color="text" display="block" pb={0.5}>Without checkout, visitors send an enquiry instead of booking.</MDTypography>
          </MDBox>
        </Grid>
      )}
    </Grid>
  );
}

BrandAssetFields.propTypes = {
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showCheckout: PropTypes.bool,
};

BrandAssetFields.defaultProps = { disabled: false, showCheckout: true };

export default BrandAssetFields;
